import React, { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate } from "@/lib/format";
import { F } from "@/pages/Employees";
import { dueText, kmText } from "@/components/VehicleDueAlert";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, History } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export const JENIS_KENDARAAN = ["Mobil Pickup", "Mobil Box", "Truk", "Dump Truk", "Mobil Penumpang", "Sepeda Motor"];
const empty = { plat_no: "", tahun: "", jenis: "", merk: "", tgl_pajak: "", tgl_stnk: "", aktif: true, catatan: "", interval_service_km: 5000, km_oli_awal: "" };
const km = (n) => Number(n || 0).toLocaleString("id-ID");

const OilDue = ({ v, testid }) => {
  if (v.sisa_km === null || v.sisa_km === undefined) return <span className="text-xs text-slate-400" data-testid={testid}>Belum ada data ganti oli</span>;
  const tone = v.sisa_km < 0 ? "bg-rose-600 text-white" : v.sisa_km <= 500 ? "bg-amber-100 text-amber-800" : "bg-emerald-50 text-emerald-700";
  return (
    <span className="whitespace-nowrap text-xs" data-testid={testid}>
      <span className="font-mono">{km(v.km_terakhir)} → {km(v.km_service_berikut)} km</span>
      <span className={`ml-1 rounded px-1.5 py-0.5 text-[11px] font-semibold ${tone}`}>{kmText(v.sisa_km)}</span>
    </span>
  );
};

const Due = ({ tgl, sisa, testid }) => {
  if (!tgl) return <span className="text-slate-300">—</span>;
  const tone = sisa < 0 ? "bg-rose-600 text-white" : sisa <= 30 ? "bg-amber-100 text-amber-800" : "bg-emerald-50 text-emerald-700";
  return <span className="whitespace-nowrap" data-testid={testid}>{formatDate(tgl)} <span className={`ml-1 rounded px-1.5 py-0.5 text-[11px] font-semibold ${tone}`}>{dueText(sisa)}</span></span>;
};

export default function VehicleMaster({ vehicles, reload }) {
  const { user } = useAuth();
  const canDelete = ["super_admin", "direktur", "finance"].includes(user?.role);
  const [form, setForm] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.plat_no.trim() || !form.jenis.trim()) return toast.error("Plat No. dan Jenis Kendaraan wajib diisi");
    const payload = { ...form, interval_service_km: Number(form.interval_service_km) || 5000, km_oli_awal: Number(form.km_oli_awal) || 0 };
    try {
      if (form.id) await api.put(`/vehicles/${form.id}`, payload); else await api.post("/vehicles", payload);
      toast.success("Kendaraan tersimpan"); setForm(null); reload();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (v) => {
    if (!window.confirm(`Hapus kendaraan ${v.plat_no}?`)) return;
    try { await api.delete(`/vehicles/${v.id}`); toast.success("Kendaraan dihapus"); reload(); } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Card className="p-0 overflow-hidden">
      <div className="flex items-center justify-between p-4 border-b">
        <div className="text-sm text-slate-500">{vehicles.length} kendaraan terdaftar</div>
        <Button className="bg-blue-800 hover:bg-blue-900" onClick={() => setForm({ ...empty })} data-testid="veh-add-btn"><Plus className="h-4 w-4 mr-1" /> Tambah Kendaraan</Button>
      </div>
      <div className="overflow-x-auto scroll-thin">
        <table className="w-full text-sm">
          <thead><tr className="bg-blue-900 text-white text-xs uppercase">
            {["Plat No", "Tahun", "Jenis Kendaraan", "Merk / Tipe", "Tgl Pajak", "Tgl STNK", "KM → Ganti Oli", "Status", ""].map((h) => <th key={h} className="px-3 py-2.5 text-left">{h}</th>)}
          </tr></thead>
          <tbody>
            {vehicles.length === 0 && <tr><td colSpan={9} className="py-10 text-center text-slate-400">Belum ada kendaraan.</td></tr>}
            {vehicles.map((v, i) => (
              <tr key={v.id} className="border-b border-slate-100" data-testid={`veh-row-${i}`}>
                <td className="px-3 py-2 font-mono font-bold whitespace-nowrap"><Link to={`/vehicles/${v.id}`} className="text-blue-800 underline-offset-2 hover:underline" data-testid={`veh-link-${i}`}>{v.plat_no}</Link></td><td className="px-3 py-2">{v.tahun || "-"}</td>
                <td className="px-3 py-2">{v.jenis}</td><td className="px-3 py-2">{v.merk || "-"}</td>
                <td className="px-3 py-2"><Due tgl={v.tgl_pajak} sisa={v.sisa_pajak} testid={`veh-pajak-${i}`} /></td>
                <td className="px-3 py-2"><Due tgl={v.tgl_stnk} sisa={v.sisa_stnk} testid={`veh-stnk-${i}`} /></td>
                <td className="px-3 py-2"><OilDue v={v} testid={`veh-oli-${i}`} /></td>
                <td className="px-3 py-2">{v.aktif === false ? <span className="text-xs text-slate-400">Nonaktif</span> : <span className="text-xs font-semibold text-emerald-700">Aktif</span>}</td>
                <td className="px-3 py-2 whitespace-nowrap text-right">
                  <Link to={`/vehicles/${v.id}`}><Button size="icon" variant="ghost" className="text-orange-600" title="Riwayat" data-testid={`veh-history-${i}`}><History className="h-4 w-4" /></Button></Link>
                  <Button size="icon" variant="ghost" onClick={() => setForm({ ...empty, ...v })} data-testid={`veh-edit-${i}`}><Pencil className="h-4 w-4" /></Button>
                  {canDelete && <Button size="icon" variant="ghost" className="text-red-600" onClick={() => del(v)} data-testid={`veh-del-${i}`}><Trash2 className="h-4 w-4" /></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={!!form} onOpenChange={(o) => { if (!o) setForm(null); }}>
        <DialogContent className="max-w-lg" data-testid="veh-dialog">
          <DialogHeader><DialogTitle>{form?.id ? "Ubah" : "Tambah"} Kendaraan</DialogTitle></DialogHeader>
          {form && <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <F label="Plat No. *"><Input value={form.plat_no} placeholder="L 1234 AB" data-testid="veh-f-plat" onChange={(e) => set("plat_no", e.target.value.toUpperCase())} /></F>
            <F label="Tahun"><Input value={form.tahun} placeholder="2020" inputMode="numeric" data-testid="veh-f-tahun" onChange={(e) => set("tahun", e.target.value)} /></F>
            <F label="Jenis Kendaraan *">
              <Input list="jenis-kendaraan" value={form.jenis} placeholder="Pilih / ketik" data-testid="veh-f-jenis" onChange={(e) => set("jenis", e.target.value)} />
              <datalist id="jenis-kendaraan">{JENIS_KENDARAAN.map((j) => <option key={j} value={j} />)}</datalist>
            </F>
            <F label="Merk / Tipe"><Input value={form.merk} placeholder="Mitsubishi L300" data-testid="veh-f-merk" onChange={(e) => set("merk", e.target.value)} /></F>
            <F label="Tgl Pajak (tahunan)"><Input type="date" value={form.tgl_pajak} data-testid="veh-f-pajak" onChange={(e) => set("tgl_pajak", e.target.value)} /></F>
            <F label="Tgl STNK (5 tahunan)"><Input type="date" value={form.tgl_stnk} data-testid="veh-f-stnk" onChange={(e) => set("tgl_stnk", e.target.value)} /></F>
            <F label="Interval Ganti Oli (km)"><Input type="number" value={form.interval_service_km} data-testid="veh-f-interval" onChange={(e) => set("interval_service_km", e.target.value)} /></F>
            <F label="KM Ganti Oli Terakhir (bila belum tercatat)"><Input type="number" value={form.km_oli_awal} placeholder="mis. 45000" data-testid="veh-f-km-oli" onChange={(e) => set("km_oli_awal", e.target.value)} /></F>
            <div className="sm:col-span-2"><F label="Catatan"><Input value={form.catatan} data-testid="veh-f-catatan" onChange={(e) => set("catatan", e.target.value)} /></F></div>
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.aktif !== false} onCheckedChange={(v) => set("aktif", v)} data-testid="veh-f-aktif" /> Kendaraan aktif</label>
          </div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>Batal</Button>
            <Button className="bg-blue-800 hover:bg-blue-900" onClick={save} data-testid="veh-save-btn">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
